import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import CampOpsNav from "@/components/CampOpsNav";
import api from "@/lib/api";
import { QrCode, Search, CheckCircle2, Clock, XCircle, Camera, CameraOff, FileText, Banknote } from "lucide-react";

const ATTENDANCE_OPTIONS = ["registered", "checked_in", "late", "no_show"];

const ATT_STYLE = {
  registered: "bg-slate-200 text-slate-700",
  checked_in: "bg-emerald-100 text-emerald-800",
  late: "bg-amber-100 text-amber-800",
  no_show: "bg-red-100 text-red-700",
};

export default function AdminCheckIn() {
  const { campId } = useParams();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const scannerRef = useRef(null);

  const load = async () => {
    const { data: d } = await api.get(`/admin/camps/${campId}/checkin-roster`);
    setData(d);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [campId]);

  const setStatus = async (reg, status) => {
    await api.patch(`/admin/registrations/${reg.id}/checkin`, { attendance_status: status });
    await load();
  };

  // Camera scanner — lazy load html5-qrcode
  const startScanner = async () => {
    setScanError("");
    setScanResult(null);
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const el = "qr-camera-region";
      const scanner = new Html5Qrcode(el);
      scannerRef.current = scanner;
      setScanning(true);
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        async (decoded) => {
          // Stop immediately to avoid duplicate hits
          await scanner.stop();
          setScanning(false);
          handleScannedValue(decoded);
        },
        () => {}
      );
    } catch (e) {
      setScanError(e?.message || "Could not start camera. Permission denied or no camera available.");
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    try {
      if (scannerRef.current) await scannerRef.current.stop();
    } catch (_) {}
    setScanning(false);
  };

  const handleScannedValue = async (decoded) => {
    // Accept either a full /qr/{token} URL or a raw token
    let token = decoded;
    try {
      const u = new URL(decoded);
      const parts = u.pathname.split("/").filter(Boolean);
      const qrIdx = parts.indexOf("qr");
      if (qrIdx >= 0 && parts[qrIdx + 1]) token = parts[qrIdx + 1];
    } catch (_) {
      /* not a URL, use as raw token */
    }
    try {
      const { data: lookup } = await api.get(`/qr/lookup/${token}`);
      const reg = data?.registrations?.find((r) => r.player_id === lookup.player.id);
      setScanResult({ lookup, registration: reg });
      if (reg && reg.attendance_status !== "checked_in") {
        // auto check-in
        await setStatus(reg, "checked_in");
      }
    } catch (e) {
      setScanError("Player not found for that QR token.");
    }
  };

  if (!data) {
    return <AdminLayout><div className="p-8 label-overline">Loading roster…</div></AdminLayout>;
  }

  const filtered = (data.registrations || []).filter((r) => {
    if (!q) return true;
    const name = `${r.player?.first_name || ""} ${r.player?.last_name || ""}`.toLowerCase();
    return name.includes(q.toLowerCase()) || (r.player?.school || "").toLowerCase().includes(q.toLowerCase());
  });

  return (
    <AdminLayout>
      <div className="p-8 max-w-7xl">
        <Link to="/admin/camps" className="label-overline text-slate-500 hover:text-[#E63946]">← All Camps</Link>
        <div className="flex flex-wrap items-end justify-between mt-3 gap-3">
          <div>
            <div className="label-overline text-[#E63946]">Event Check-In</div>
            <h1 data-testid="checkin-page-title" className="font-display text-4xl uppercase tracking-tighter mt-2">
              {data.camp.title}
            </h1>
            <div className="text-xs text-slate-500 mt-1">
              {data.camp.city}, {data.camp.province} · {new Date(data.camp.start_date).toLocaleDateString("en-CA", { dateStyle: "medium" })}
            </div>
          </div>
        </div>

        <div className="mt-6">
          <CampOpsNav campId={campId} />
        </div>

        {/* Summary tiles */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: "Total", val: data.summary.total, color: "text-[#0A192F]" },
            { label: "Registered", val: data.summary.registered, color: "text-slate-600" },
            { label: "Checked In", val: data.summary.checked_in, color: "text-emerald-700" },
            { label: "Late", val: data.summary.late, color: "text-amber-700" },
            { label: "No Show", val: data.summary.no_show, color: "text-red-700" },
          ].map((t) => (
            <div key={t.label} className="bg-white border border-slate-200 p-4 rounded-sm">
              <div className="label-overline">{t.label}</div>
              <div className={`font-data text-3xl mt-1 ${t.color}`}>{t.val}</div>
            </div>
          ))}
        </div>

        {/* Scanner */}
        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg uppercase tracking-tight">
                <QrCode className="inline w-5 h-5 mr-2 text-[#E63946]" />
                QR Scanner
              </h3>
              {!scanning ? (
                <button data-testid="start-scanner" onClick={startScanner} className="inline-flex items-center gap-1 bg-[#E63946] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm">
                  <Camera className="w-4 h-4" /> Start Camera
                </button>
              ) : (
                <button onClick={stopScanner} className="inline-flex items-center gap-1 border border-slate-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm">
                  <CameraOff className="w-4 h-4" /> Stop
                </button>
              )}
            </div>
            <div id="qr-camera-region" className="mt-4 min-h-[240px] bg-slate-100 grid place-items-center border border-slate-200">
              {!scanning && <span className="text-xs text-slate-500">Camera off · Start to scan a player card</span>}
            </div>
            {scanError && <div className="mt-3 border-l-4 border-[#E63946] bg-red-50 p-2 text-xs text-[#E63946]">{scanError}</div>}
            {scanResult && (
              <div className="mt-4 border border-emerald-300 bg-emerald-50 p-4">
                <div className="flex items-center gap-2 text-emerald-800 font-semibold uppercase text-xs tracking-wider">
                  <CheckCircle2 className="w-4 h-4" /> Scan Successful
                </div>
                <div className="mt-2 text-sm">
                  <span className="font-display text-lg">{scanResult.lookup.player.first_name} {scanResult.lookup.player.last_name}</span>
                  <span className="text-xs text-slate-500 ml-2">{scanResult.lookup.player.graduation_year} · {scanResult.lookup.player.primary_position}</span>
                </div>
                {scanResult.registration ? (
                  <div className="text-xs mt-1">Checked in to <strong>{data.camp.title}</strong></div>
                ) : (
                  <div className="text-xs text-red-700 mt-1">⚠️ Player is not registered for this camp</div>
                )}
              </div>
            )}
          </div>

          {/* Quick manual check-in by player search */}
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h3 className="font-display text-lg uppercase tracking-tight">
              <Search className="inline w-5 h-5 mr-2 text-[#E63946]" />
              Manual Search
            </h3>
            <input
              data-testid="checkin-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search player name or school…"
              className="mt-3 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
            />
            <p className="text-xs text-slate-500 mt-2">Use the roster below to set attendance status for any registered player.</p>
          </div>
        </div>

        {/* Roster */}
        <div className="mt-8 bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-display text-lg uppercase tracking-tight">Roster ({filtered.length})</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left p-3 label-overline">#</th>
                <th className="text-left p-3 label-overline">Player</th>
                <th className="text-left p-3 label-overline">Class / Pos</th>
                <th className="text-left p-3 label-overline">Payment</th>
                <th className="text-left p-3 label-overline">Waiver</th>
                <th className="text-left p-3 label-overline">Attendance</th>
                <th className="text-right p-3 label-overline">Set Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`checkin-row-${r.id}`} className="border-b border-slate-100 last:border-0">
                  <td className="p-3">
                    <Link to={`/admin/players/${r.player?.id}`} className="font-semibold hover:text-[#E63946]">
                      {r.player ? `${r.player.first_name} ${r.player.last_name}` : "—"}
                    </Link>
                    <div className="text-xs text-slate-500">{r.player?.school || ""}</div>
                  </td>
                  <td className="p-3 font-data text-xs">{r.player?.graduation_year || "—"} · {r.player?.primary_position || "—"}</td>
                  <td className="p-3">
                    {r.payment?.status === "paid" || r.amount_due === 0 ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><Banknote className="w-3.5 h-3.5" /> {r.amount_due === 0 ? "Comp" : "Paid"}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-700 text-xs"><Clock className="w-3.5 h-3.5" /> Unpaid</span>
                    )}
                  </td>
                  <td className="p-3">
                    {r.waiver ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><FileText className="w-3.5 h-3.5" /> Signed</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-700 text-xs"><XCircle className="w-3.5 h-3.5" /> Missing</span>
                    )}
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-sm ${ATT_STYLE[r.attendance_status] || "bg-slate-100"}`}>
                      {r.attendance_status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      {ATTENDANCE_OPTIONS.map((opt) => (
                        <button
                          key={opt}
                          data-testid={`checkin-${opt}-${r.id}`}
                          onClick={() => setStatus(r, opt)}
                          disabled={r.attendance_status === opt}
                          className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-sm ${
                            r.attendance_status === opt ? "bg-[#0A192F] text-white" : "border border-slate-300 hover:bg-slate-50"
                          }`}
                        >
                          {opt === "checked_in" ? "Check In" : opt.replace("_", " ")}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="7" className="p-8 text-center text-sm text-slate-500">No matches.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
